/**
 * Card Component
 * Minimal borders. No shadows. No rounded corners.
 */

import { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export default function Card({
  children,
  className = '',
  interactive = false,
  padding = 'md',
}: CardProps) {
  const paddings = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const baseClass = interactive ? 'card-interactive' : 'card';

  return (
    <div className={`${baseClass} ${paddings[padding]} ${className}`}>
      {children}
    </div>
  );
}

/* ========== Card Header ========== */

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className = '' }: CardHeaderProps) {
  return (
    <div className={`border-b border-border-subtle px-6 py-4 ${className}`}>
      {children}
    </div>
  );
}

/* ========== Card Title ========== */

interface CardTitleProps {
  children: ReactNode;
  className?: string;
}

export function CardTitle({ children, className = '' }: CardTitleProps) {
  return (
    <h3 className={`text-h4 font-medium text-text-primary ${className}`}>
      {children}
    </h3>
  );
}

/* ========== Card Content ========== */

interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export function CardContent({ children, className = '' }: CardContentProps) {
  return <div className={`p-6 ${className}`}>{children}</div>;
}

/* ========== Card Footer ========== */

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export function CardFooter({ children, className = '' }: CardFooterProps) {
  return (
    <div className={`border-t border-border-subtle px-6 py-4 bg-surface-subtle ${className}`}>
      {children}
    </div>
  );
}
